import { Injectable } from '@angular/core';
import { reduce, isArray, get, isNil, unset, isEmpty, isEqual } from 'lodash';

import { decimalNumber, DecimalNumber } from '@app/shared/common';

@Injectable()
export class ReallyChangeDetectService {
  /**
   * get really changed item by fields
   *
   * @param newItem new stand worksheet item
   * @param originItem origin stand worksheet item, will use this to judge is changed
   * @param compareFields compare item field, string(single field) or string arr(multi-fields)
   * @param alreadyChanged already changed item now, @default {}
   */
  public getReallyChangedItem<T>(
    newItem: T,
    originItem: T,
    compareFields: string | string[],
    alreadyChanged?: Partial<T>,
  ): Partial<T> {
    const alreadyChangedWithDefault = alreadyChanged ?? {};

    if (isEmpty(compareFields)) {
      return alreadyChangedWithDefault;
    }

    const changedItem = reduce(
      isArray(compareFields) ? compareFields : [compareFields],
      (changedData, field) => {
        const newValue = get(newItem, field);
        const originValue = get(originItem, field);

        const isNewValueNil = isNil(newValue);
        const isOriginValueNil = isNil(originValue);

        if (
          // new value nil and original value not nil
          (isNewValueNil && !isOriginValueNil) ||
          // new value not nil and original value nil
          (!isNewValueNil && isOriginValueNil) ||
          // new value and original value not nil
          // and ((decimal is equal when it's decimal) or (just judge is equal when it's not valid number))
          (!isNewValueNil &&
            !isOriginValueNil &&
            !(DecimalNumber.isValidDecimal(newValue)
              ? decimalNumber(newValue).eq(originValue)
              : isEqual(newValue, originValue)))
        ) {
          // changed, set target field to new value
          changedData[field] = newValue;
        } else {
          // not changed, need remove target field if exist
          unset(changedData, field);
        }

        return changedData;
      },
      alreadyChangedWithDefault,
    );

    return changedItem;
  }
}
